import _ from 'lodash';
import {
  getAuth,
} from 'firebase/auth';
import {
  getDatabase,
  ref,
  onValue,
} from 'firebase/database';
import moment from 'moment';
import { eventChannel } from 'redux-saga';
import {
  all,
  takeEvery,
  takeLatest,
  fork,
  put,
  take,
  call,
  select,
} from 'redux-saga/effects';
import axios from 'axios';
import actions from './actions';
import authActions from '../auth/actions';
import { notification } from '../../components';

const ROOT_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_ROOT_URL;
const TIME_FORMAT = 'YYYY-MM-DD HH:mm';

const getAppointmentInfoFromStore = (state) => state.PatientAppointments.appointmentInfo;

function generateCorrectLink(shortLink) {
  const diff = 20 - shortLink.length;
  let newShortLink = _.cloneDeep(shortLink);
  if (diff > 0) {
    for (let i = 0; i < diff; i += 1) {
      newShortLink = `${newShortLink}-`;
    }
  }
  return newShortLink;
}

function getIdToken() {
  return getAuth().currentUser.getIdToken();
}

function getAppointmentInfoFromCloudFunction(shortLink, path) {
  return axios.post(
    `${ROOT_URL}/getAppointmentInfo`,
    { id: shortLink, path },
  );
}

export function* requestAppointmentInfo() {
  yield takeLatest(actions.APPOINTMENT_INFO_REQUEST, function* (action) {
    try {
      const { shortLink, path } = yield call(generateCorrectLink, action.payload);
      const { data } = yield call(getAppointmentInfoFromCloudFunction, shortLink, path);
      yield put({
        type: actions.APPOINTMENT_INFO_REQUEST_SUCCESS,
        payload: {
          data,
          link: shortLink,
        },
      });
    } catch (error) {
      console.warn(error);
      if (error?.response?.data?.data === 'No patient CPF.') {
        yield put({
          type: actions.NO_PATIENT_CPF,
        });
      } else {
        yield put({
          type: actions.APPOINTMENT_INFO_REQUEST_REJECTED,
        });
      }
    }
  });
}

function respondAppointmentInCloudFunction({
  shortLink,
  action,
  appointment,
}) {
  return axios.post(
    `${ROOT_URL}/patientRespondedNotification`,
    {
      id: shortLink,
      action,
      appointmentInfo: appointment,
    },
  );
}

export function* requestRespondAppointment() {
  yield takeLatest(actions.PATIENT_RESPONDED_REQUEST, function* (action) {
    try {
      yield put({
        type: actions.PATIENT_RESPONDED_REQUEST_PENDING,
        payload: action.payload.action,
      });
      const { data } = yield call(respondAppointmentInCloudFunction, action.payload);
      if (data === 'confirmed') {
        yield put({
          type: actions.APPOINTMENT_INFO_REQUEST,
          payload: {
            shortLink: action.payload.shortLink,
            path: 'confirmation',
          },
        });
      } else if (data === 'canceled') {
        yield put({
          type: actions.PATIENT_RESPONDED_REQUEST_REJECTED,
        });
      }
    } catch (error) {
      console.warn(error);
      const titles = {
        cancel: 'Não foi possível cancelar o agendamento',
        confirm: 'Não foi possível confirmar o agendamento',
      };
      notification(
        'error',
        titles[action.payload.action] || 'Não foi possível responder ao agendamento',
        'Entre em contato com a clínica.',
      );
      yield put({
        type: actions.PATIENT_RESPONDED_REQUEST_ERROR,
      });
    }
  });
}

function cancelPendingRequestInCloudFunction(key, idToken) {
  return axios.post(
    `${ROOT_URL}/cancelAppointmentRequest`,
    { key },
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

export function* requestCancelPending() {
  yield takeLatest(actions.PATIENT_CANCEL_REQUEST, function* (action) {
    try {
      const idToken = yield call(getIdToken);
      yield call(cancelPendingRequestInCloudFunction, action.payload, idToken);
      yield put({ type: actions.PATIENT_CANCEL_REQUEST_SUCCESS });
    } catch (error) {
      console.warn(error);
      notification('error', 'Não foi possível desistir do pedido', 'Entre em contato com a clínica.');
      yield put({ type: actions.PATIENT_CANCEL_REQUEST_ERROR });
    }
  });
}

// Os quatro nós de users/{uid}/appointments têm o mesmo formato e o mesmo ciclo de vida.
const APPOINTMENT_NODES = [
  { node: 'confirmed', flag: 'confirmedSection', successType: actions.PATIENT_CONFIRMED_FETCH_SUCCESS },
  { node: 'pending', flag: 'pendingSection', successType: actions.PATIENT_PENDING_FETCH_SUCCESS },
  { node: 'rejected', flag: 'rejectedSection', successType: actions.PATIENT_REJECTED_FETCH_SUCCESS },
  { node: 'canceled', flag: 'canceledSection', successType: actions.PATIENT_CANCELED_FETCH_SUCCESS },
];

function createNodeListener(node) {
  const db = getDatabase();
  const { currentUser } = getAuth();
  return eventChannel((emit) => {
    const nodeRef = ref(db, `users/${currentUser.uid}/appointments/${node}`);
    const unsubscribe = onValue(nodeRef, (snapshot) => (
      emit(snapshot.val() || {})
    ));
    return () => unsubscribe();
  });
}

function normalizeAppointments(items, flag) {
  return _.map(items, (val, id) => {
    const start = moment(val.time, TIME_FORMAT);
    return {
      ...val,
      id,
      start: start.toDate(),
      end: start.clone().add(moment.duration(val.duration).asMinutes(), 'm').toDate(),
      [flag]: true,
      allDay: false,
    };
  });
}

function watchAppointmentNode({ node, flag, successType }) {
  return function* () {
    let currentListener = null;
    yield takeLatest(actions.PATIENT_APPOINTMENTS_FETCH, function* () {
      if (currentListener) {
        currentListener.close();
        currentListener = null;
      }
      if (!getAuth().currentUser) return;
      currentListener = yield call(createNodeListener, node);
      yield takeEvery(currentListener, function* (items) {
        yield put({
          type: successType,
          payload: {
            id: node,
            data: normalizeAppointments(items, flag),
          },
        });
      });
      yield take([authActions.LOGOUT, authActions.CLEAR_STATES]);
      currentListener.close();
      currentListener = null;
    });
  };
}

function getTelemedDocumentFromDB() {
  const db = getDatabase();
  return new Promise((resolve) => {
    onValue(ref(db, 'utils/telemed'), resolve, { onlyOnce: true });
  });
}

export function* getTelemedDocument() {
  yield takeEvery(actions.FETCH_TELEMED_DOCUMENT_REQUEST, function* () {
    try {
      yield put({ type: actions.FETCHING_TELEMED_DOCUMENT });
      const telemed = yield call(getTelemedDocumentFromDB);
      if (telemed.val()) {
        const { version, document } = telemed.val();
        yield put({
          type: actions.FETCH_TELEMED_DOCUMENT_SUCCESS,
          payload: {
            version,
            document,
          },
        });
      }
    } catch (error) {
      console.warn(error);
      yield put({ type: actions.FETCH_TELEMED_DOCUMENT_ERROR });
    }
  });
}

function acceptTelemedComplianceOnDB(
  queryConfirmed,
) {
  const bodyParameters = {
    queryConfirmed,
  };
  return axios.post(
    `${ROOT_URL}/acceptTelemedCompliance`,
    bodyParameters,
  );
}

export function* requestAcceptTelemedCompliance() {
  yield takeLatest(actions.ACCEPT_TELEMED_COMPLIANCE_REQUEST, function* () {
    try {
      yield put({ type: actions.UPDATING_TELEMED_COMPLIANCE });
      const { queryConfirmed } = yield select(getAppointmentInfoFromStore);
      const { status } = yield call(acceptTelemedComplianceOnDB, queryConfirmed);
      if (status === 201) {
        yield all([
          put({
            type: actions.APPOINTMENT_INFO_REQUEST,
            payload: {
              shortLink: queryConfirmed.queryConfirmedKey || queryConfirmed.batchedId,
              path: 'telemed',
            },
          }),
        ]);
      }
    } catch (error) {
      console.warn(error);
      yield put({
        type: actions.ACCEPT_TELEMED_COMPLIANCE_ERROR,
      });
    }
  });
}

export default function* rootSaga() {
  yield all([
    ...APPOINTMENT_NODES.map((entry) => fork(watchAppointmentNode(entry))),
    fork(requestAppointmentInfo),
    fork(requestRespondAppointment),
    fork(requestCancelPending),
    fork(getTelemedDocument),
    fork(requestAcceptTelemedCompliance),
  ]);
}
